import { useMemo } from "react";
import { cn } from "@/lib/utils";
import { DayTenantColumn } from "./day-tenant-column";
import { BlockChip, blocksForColumn, isDayClosingBlock } from "../calendar-blocks";
import { formatHourLabel, formatSubHourLabel, timeGridWidthStyle } from "../calendar-utils";

// Minimum column widths for the fixed-width modes. When the total of N * min
// exceeds the available canvas, horizontal scrolling kicks in. "dynamic" keeps
// the original behavior — columns share width equally with no minimum.
const COLUMN_MIN_WIDTH_PX = {
  dynamic: 0,
  comfortable: 240,
  spacious: 320,
};

function getMinColumnPx(mode, customPx) {
  if (mode === "custom") {
    const n = Number(customPx);
    return Number.isFinite(n) && n > 0 ? n : COLUMN_MIN_WIDTH_PX.comfortable;
  }
  if (Object.prototype.hasOwnProperty.call(COLUMN_MIN_WIDTH_PX, mode)) {
    return COLUMN_MIN_WIDTH_PX[mode];
  }
  return COLUMN_MIN_WIDTH_PX.dynamic;
}

/**
 * Time-labels column. Lives in the pane's own time gutter so it can stay
 * sticky-left during horizontal scroll when tenant columns are wider than the
 * viewport.
 *
 * Two stacked grids share one cell:
 *   1. the TICK grid, matching the tenant columns' row template. It draws no text
 *      any more — it exists purely to carry the `data-hour` scroll anchors on the
 *      same rows the columns use.
 *   2. the FINE-lattice label grid, carrying the actual time text. Labels come
 *      from `timeAxis.labels`, whose density the axis derives from the zoom, so
 *      sub-hour labels (:30, :15, …) appear as soon as there is room for them —
 *      independently of the user's gridline interval.
 *
 * The anchors deliberately stay on the tick layer: the toolbar's AM/PM jump does
 * scrollIntoView on `[data-hour]`, and moving them onto the (offset) label items
 * would shift where it lands.
 */
function HourLabelsGutter({ timeAxis, use12HourFormat }) {
  return (
    <div className="sticky left-0 z-30 w-14 flex-none bg-background ring-1 ring-border/60">
      <div className="grid grid-cols-1 grid-rows-1">
        <div
          style={{ gridTemplateRows: timeAxis.tickRowTemplate }}
          className="col-start-1 col-end-2 row-start-1 grid grid-cols-1"
        >
          <div className="row-end-1 h-7" />
          {timeAxis.ticks.map((tick) => (
            // Hour rows carry a `data-hour` anchor + scroll-margin so the toolbar's
            // AM/PM session jumps can scrollIntoView here and land just below the
            // sticky "All day" bar.
            <div
              key={tick.key}
              data-hour={tick.isHour ? tick.hour : undefined}
              className={tick.isHour ? "scroll-mt-16" : undefined}
            />
          ))}
        </div>

        <div
          style={{ gridTemplateRows: timeAxis.fineRowTemplate }}
          className="pointer-events-none col-start-1 col-end-2 row-start-1 grid grid-cols-1"
        >
          {timeAxis.labels.map((label) => (
            <div
              key={label.key}
              style={{ gridRow: `${label.row} / span 1` }}
              className={cn(
                "col-start-1 col-end-2 -mt-2.5 whitespace-nowrap pr-2 text-right",
                label.isHour
                  ? "text-xs/5 text-muted-foreground"
                  : "text-[10px]/5 text-muted-foreground/60",
              )}
            >
              {label.isHour
                ? formatHourLabel(label.hour, { hour12: use12HourFormat })
                : formatSubHourLabel(label.startMinute, { hour12: use12HourFormat })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * Renders one calendar day as a fully self-contained pane: its own time-ruler
 * (hour-labels gutter), its own all-day chips, and its column(s) of events. The
 * day view mounts one of these per visible day; in the "2 days" split each pane
 * lives in its own scroll container behind a draggable divider, so each renders
 * its own ruler rather than sharing one. The per-pane date selector is a fixed
 * bar OUTSIDE this scroll content (see DayPaneScroller) so it stays centered and
 * never scrolls with the timeline.
 */
export function DayPane({
  pane,
  /** Index of this pane among the visible days — keeps DnD droppable ids unique. */
  paneIndex = 0,
  use12HourFormat,
  timeAxis,
  selectedEventId,
  setSelectedEventId,
  /** Deep-linked appointment ("show this on the calendar") — ringed until dismissed. */
  highlightedEventId = null,
  currentTimeRowStart,
  currentTimeRowOffsetPercent,
  nowAnchorRef,
  timeOffBlocks,
  doctorOptions,
  columnWidthMode = "dynamic",
  columnWidthCustomPx,
  widthZoomPercent = 100,
  dayViewGroupBy = "tenant",
  dragPlaceholder = null,
  showSessionRail = false,
  railWidthPx = 26,
  railColorMode = "accent",
  railMarginPx = 0,
  /** (columnId, paneDate) → open the session override dialog (doctor grouping only). */
  onSessionAction,
  /** ({ calendarId, dayKey, minute }) → open the "New entry" sheet (standalone-calendar columns). */
  onCreateCalendarEntry,
}) {
  const splitColumns = Boolean(pane.splitColumns);
  const columnsPerPane = pane.columnsToRender?.length || 1;

  // Per-column time-off blocks keyed by column id. In doctor grouping a doctor's
  // own block (vacation, ooo, …) shows only in their column and paints that
  // column's reserved hatch; global holidays still show — and paint — every
  // column. Tenant grouping and the merged "_all" column keep showing every
  // block, with only clinic-wide (global) closures painting the overlay.
  // Standalone-calendar columns belong to no doctor: global closures only.
  const perColumn = useMemo(() => {
    const map = new Map();
    for (const col of pane.columnsToRender) {
      const chips = blocksForColumn(timeOffBlocks, pane.date, {
        splitColumns: pane.splitColumns,
        groupBy: dayViewGroupBy,
        columnId: col.id,
        kind: col.kind,
      });
      const isDoctorColumn =
        pane.splitColumns &&
        dayViewGroupBy === "doctor" &&
        col.id !== "_all" &&
        col.kind !== "calendar";
      const overlay = chips.filter(
        (b) => isDayClosingBlock(b) && (isDoctorColumn || b.scope === "global"),
      );
      map.set(col.id, { chips, overlay });
    }
    return map;
  }, [pane, timeOffBlocks, dayViewGroupBy]);

  const doctorNameById = useMemo(() => {
    const map = new Map();
    if (Array.isArray(doctorOptions)) {
      for (const opt of doctorOptions) {
        const id = String(opt?.id || "").trim();
        const name = String(opt?.name || "").trim();
        if (id && name) map.set(id, name);
      }
    }
    return map;
  }, [doctorOptions]);

  const renderChips = (dayBlocks) =>
    dayBlocks.map((block) => (
      <BlockChip
        key={block.id}
        block={block}
        doctorName={
          block.scope !== "global" && block.ownerId
            ? doctorNameById.get(block.ownerId) || null
            : null
        }
      />
    ));

  // "dynamic" keeps `minmax(0, 1fr)` so columns share width and never overflow.
  // Fixed modes set a min-width per column; when the columns exceed the pane's
  // viewport the pane's own ScrollArea picks up the horizontal scroll.
  const minColPx = splitColumns ? getMinColumnPx(columnWidthMode, columnWidthCustomPx) : 0;
  const gridTemplateColumns = splitColumns
    ? `repeat(${columnsPerPane}, minmax(${minColPx}px, 1fr))`
    : undefined;
  // Force the body to expand so the Radix viewport sees content wider than itself and arms
  // the horizontal scrollbar. Two independent widening sources, combined with CSS max():
  //   • fixed column-width modes  → gutter + N*min px (N = this pane's columns)
  //   • horizontal ("width") zoom → a percentage of the viewport (>100% overflows → scroll)
  // Without either the grid just fills the pane (no horizontal scroll).
  const widthZoomFactor = Math.max(Number(widthZoomPercent) || 100, 0) / 100;
  const minWidthParts = [];
  if (minColPx > 0) {
    minWidthParts.push(`${56 + columnsPerPane * minColPx}px`);
  }
  if (widthZoomFactor > 1) {
    minWidthParts.push(`${100 * widthZoomFactor}%`);
  }
  // Below 100% the zoom pulls the body IN instead, so the columns render
  // narrower than the pane (flush left) — see timeGridWidthStyle. A fixed
  // column-width mode still wins: its min-width outranks that width, so the grid
  // never shrinks past its declared columns.
  const bodyWidthStyle = {
    ...(minWidthParts.length > 0
      ? {
          minWidth:
            minWidthParts.length === 1 ? minWidthParts[0] : `max(${minWidthParts.join(", ")})`,
        }
      : {}),
    ...(widthZoomFactor < 1 ? timeGridWidthStyle(widthZoomPercent) : {}),
  };
  const bodyMinWidthStyle =
    Object.keys(bodyWidthStyle).length === 0 ? undefined : bodyWidthStyle;

  // Sticky stack inside the scroll content: the all-day strip (min-h-8) sticks to
  // the top of the viewport, then the per-column title rows sit just below it. The
  // date-selector heading is NOT here — it's a fixed bar above the ScrollArea.
  const stripStickyTopClass = "top-0";
  const titleStickyTopClass = "top-8";

  return (
    <div
      className="flex min-h-full w-full flex-auto flex-col"
      style={bodyMinWidthStyle}
    >
      <div
        className={cn(
          "sticky z-40 flex w-full items-stretch border-b border-border/60 bg-background shadow-[0_1px_0_0_hsl(var(--border))]",
          stripStickyTopClass,
        )}
      >
        {/* Time-gutter spacer + "All day" label. Sticks at the top of the
            scroll container so chips stay visible during vertical scroll, and
            sticks at the left during horizontal scroll so the corner stays
            anchored over the hour-labels gutter below. */}
        <div className="sticky left-0 z-10 flex w-14 flex-none items-center justify-end bg-background pr-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          All day
        </div>
        <div className="flex min-w-0 flex-1">
          {splitColumns ? (
            <div
              className="grid h-full min-w-0 divide-x divide-border/60"
              style={{ gridTemplateColumns }}
            >
              {pane.columnsToRender.map((col) => (
                <div
                  key={col.id}
                  className="flex min-h-8 min-w-0 flex-wrap items-center gap-1.5 px-2 py-1.5"
                >
                  {renderChips(perColumn.get(col.id)?.chips || [])}
                </div>
              ))}
            </div>
          ) : (
            <div className="flex h-full min-h-8 min-w-0 flex-wrap items-center gap-1.5 px-3 py-1.5">
              {renderChips(perColumn.get("_all")?.chips || [])}
            </div>
          )}
        </div>
      </div>

      <div className="flex w-full flex-auto">
        <HourLabelsGutter timeAxis={timeAxis} use12HourFormat={use12HourFormat} />
        <div
          className={cn(
            "relative flex min-h-0 min-w-0 flex-1",
            splitColumns && "divide-x divide-border/60",
          )}
          style={splitColumns ? { display: "grid", gridTemplateColumns } : undefined}
        >
          {pane.columnsToRender.map((col, index) => (
            <DayTenantColumn
              key={col.id}
              events={col.events}
              tenantTitle={splitColumns ? col.title : null}
              use12HourFormat={use12HourFormat}
              timeAxis={timeAxis}
              selectedEventId={selectedEventId}
              setSelectedEventId={setSelectedEventId}
              highlightedEventId={highlightedEventId}
              currentTimeRowStart={currentTimeRowStart}
              currentTimeRowOffsetPercent={currentTimeRowOffsetPercent}
              nowAnchorRef={nowAnchorRef}
              attachNowScrollRef={index === 0}
              overlayBlocks={perColumn.get(col.id)?.overlay || []}
              dayKey={pane.dayKey}
              columnId={splitColumns ? col.id : null}
              groupBy={dayViewGroupBy}
              dragPlaceholder={dragPlaceholder}
              paneIndex={paneIndex}
              titleStickyTopClass={titleStickyTopClass}
              showSessionRail={showSessionRail}
              railWidthPx={railWidthPx}
              railColorMode={railColorMode}
              railMarginPx={railMarginPx}
              columnColorHex={col.colorHex || ""}
              onSessionAction={
                onSessionAction && col.kind !== "calendar"
                  ? (columnId) => onSessionAction(columnId, pane.date)
                  : undefined
              }
              onCreateEntry={
                col.kind === "calendar" && col.calendarId && onCreateCalendarEntry
                  ? (minute) =>
                      onCreateCalendarEntry({
                        calendarId: col.calendarId,
                        dayKey: pane.dayKey,
                        minute,
                      })
                  : undefined
              }
            />
          ))}
        </div>
      </div>
    </div>
  );
}
